import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { payments, isNativeAndroid, initPlayBilling } from "@/lib/payments";
import { ArrowLeft, ShoppingBag, Loader2, Check, Flame, Store, Ticket, X as XIcon, Gift, ThumbsUp, ThumbsDown, BadgeCheck, Flag, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import OwnershipDisclaimer from "@/components/OwnershipDisclaimer";
import AdSlot from "@/components/AdSlot";
import { CATEGORIES, CATEGORY_LABEL } from "@/lib/categories";
import { SuggestedChip, SuggestedFeed } from "@/components/SuggestedFeed";
import { ProductCover } from "@/components/ProductCover";
import { WhatsInside } from "@/components/WhatsInside";
import { CommunitySheet, FollowingChip } from "@/components/FollowingSheet";
import { GroupsChip, GroupsBar, GroupsDirectory } from "@/components/groups/GroupsBar";
import PayFromWalletDialog from "@/components/PayFromWalletDialog";
import { useWallet } from "@/lib/wallet";
import { useAuth } from "@/lib/auth";
import ProductComments from "@/components/ProductComments";
import ReportDialog from "@/components/ReportDialog";
import { MarketSearchBar, CreatorResults } from "@/components/MarketSearch";
import { MarketSort } from "@/components/MarketSort";
import { PriceFilter, EMPTY_PRICE, priceParams } from "@/components/PriceFilter";
import { SaveButton } from "@/components/SaveButton";

const MAX_POLL_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2500;

export default function Marketplace() {
  const [products, setProducts] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [owned, setOwned] = useState(new Set());
  const [polling, setPolling] = useState(false);
  const [category, setCategory] = useState(() => (new URLSearchParams(window.location.search).get("group") ? "group" : "all"));
  const [query, setQuery] = useState("");
  const [creators, setCreators] = useState([]);
  const [sort, setSort] = useState("popular");
  const [price, setPrice] = useState(EMPTY_PRICE);
  const [groupId, setGroupId] = useState(() => {
    const g = new URLSearchParams(window.location.search).get("group");
    return g && g !== "1" ? g : null;
  });
  const [groupQuery, setGroupQuery] = useState("");
  const [myGroups, setMyGroups] = useState(null);
  const [dirQuery, setDirQuery] = useState("");
  const groupsChecked = myGroups !== null;
  const groupOpen = myGroups?.find((g) => g.group_id === groupId)?.status === "active";
  const inGroup = category === "group";
  const listParams = () => (inGroup
    ? { category: "group", group_id: groupId, ...(groupQuery ? { q: groupQuery } : {}), sort, ...priceParams(price) }
    : { ...(category === "all" ? {} : { category }), ...(query ? { q: query } : {}), sort, ...priceParams(price) });
  const [codeInput, setCodeInput] = useState("");
  const [codeBusy, setCodeBusy] = useState(false);
  const [codeMatch, setCodeMatch] = useState(null);
  const pollingRef = useRef(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const myUserId = user?.user_id;
  const [reportTarget, setReportTarget] = useState(null); // { type, id, label }
  const highlightProductId = searchParams.get("product");
  const highlightedRef = useRef(false);
  const [saved, setSaved] = useState(new Set());
  const linkCode = searchParams.get("code");

  useEffect(() => {
    const load = () => api.get("/saved/ids").then((r) => setSaved(new Set(r.data))).catch(() => {});
    load();
    window.addEventListener("notelosh:saved-changed", load);
    return () => window.removeEventListener("notelosh:saved-changed", load);
  }, []);

  useEffect(() => { if (linkCode) { setCodeInput(linkCode); lookupCode(linkCode); } }, [linkCode]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Cast / toggle / clear a vote on a product. Optimistic: we flip the local
   * card first, then reconcile with the server response (which carries the
   * fresh counts and the authoritative `verified` flag).
   */
  const voteOn = useCallback(async (p, nextVote) => {
    if (myUserId && p.seller_id === myUserId) {
      toast.info("You can't rate your own note");
      return;
    }
    const isOwned = owned.has(p.product_id) || p.owned_by_me;
    if (!isOwned) {
      toast.info("Only buyers of this note can rate it");
      return;
    }
    const same = p.my_vote === nextVote;
    const payload = { vote: same ? null : nextVote };
    const patch = (list) => list.map((x) => {
      if (x.product_id !== p.product_id) return x;
      const prev = x.my_vote;
      const nxt = same ? null : nextVote;
      let likes = x.likes || 0;
      let dislikes = x.dislikes || 0;
      if (prev === "like") likes -= 1;
      if (prev === "dislike") dislikes -= 1;
      if (nxt === "like") likes += 1;
      if (nxt === "dislike") dislikes += 1;
      likes = Math.max(0, likes); dislikes = Math.max(0, dislikes);
      return { ...x, my_vote: nxt, likes, dislikes, verified: likes >= 20 };
    });
    setProducts((prev) => patch(prev));
    setTrending((prev) => patch(prev));
    try {
      const r = await api.post(`/products/${p.product_id}/vote`, payload);
      const merge = (list) => list.map((x) => x.product_id === p.product_id
        ? { ...x, my_vote: r.data.my_vote, likes: r.data.likes, dislikes: r.data.dislikes, verified: r.data.verified }
        : x);
      setProducts((prev) => merge(prev));
      setTrending((prev) => merge(prev));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save vote");
      // Revert optimistic patch by refetching current page.
      try {
        const fresh = await api.get("/products", { params: listParams() });
        setProducts(fresh.data);
      } catch { /* ignore */ }
    }
  }, [owned, category, query, sort, price, groupId, groupQuery]); // eslint-disable-line react-hooks/exhaustive-deps
  const native = isNativeAndroid();
  const wallet = useWallet();
  const [payDialogProduct, setPayDialogProduct] = useState(null);

  useEffect(() => {
    if (!inGroup) return;
    api.get("/groups/directory", { params: dirQuery ? { q: dirQuery } : {} }).then((r) => {
      setMyGroups(r.data);
    }, () => setMyGroups([]));
  }, [inGroup, dirQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (category === "suggested") return;
    if (inGroup && (!groupOpen || !groupsChecked)) { setProducts([]); setLoading(!groupsChecked); return; }
    setLoading(true);
    (async () => {
      try {
        const r = await api.get("/products", { params: listParams() });
        setProducts(r.data);
        // Hydrate the Owned set from the server so "Owned" shows up on
        // reload for anything already in the user's workspace.
        const pre = (r.data || []).filter((p) => p.owned_by_me).map((p) => p.product_id);
        if (pre.length) setOwned((prev) => new Set([...prev, ...pre]));
      } catch {
        setProducts([]);
      } finally { setLoading(false); }
    })();
  }, [category, query, sort, price, groupId, groupQuery, groupsChecked, groupOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setQuery(""); setDirQuery(""); }, [category]);

  useEffect(() => {
    if (!query) { setCreators([]); return; }
    api.get("/sellers/search", { params: { q: query } }).then((r) => setCreators(r.data || [])).catch(() => setCreators([]));
  }, [query]);

  const lookupCode = async (e) => {
    if (typeof e !== "string") e?.preventDefault?.();
    const code = (typeof e === "string" ? e : codeInput).trim().toUpperCase();
    if (!code) return;
    setCodeBusy(true);
    try {
      const r = await api.get(`/products/by-code/${encodeURIComponent(code)}`);
      setCodeMatch(r.data);
      toast.success(`Found "${r.data.name}" by ${r.data.seller_name}`);
    } catch (err) {
      setCodeMatch(null);
      toast.error(err?.response?.data?.detail || "No listing found for that code");
    } finally {
      setCodeBusy(false);
    }
  };

  const clearCodeMatch = () => { setCodeMatch(null); setCodeInput(""); };

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/products/trending", { params: { limit: 6 } });
        const list = r.data || [];
        setTrending(list);
        // Pre-fill owned Set so the "Owned" button renders immediately on
        // trending cards for things the user already has.
        const pre = list.filter((p) => p.owned_by_me).map((p) => p.product_id);
        if (pre.length) setOwned((prev) => new Set([...prev, ...pre]));
      } catch { /* non-critical */ }
    })();
  }, []);

  // Init Play Billing once when running on Android
  useEffect(() => {
    if (native) initPlayBilling();
  }, [native]);

  const pollStatus = useCallback(async (session_id, attempt = 0) => {
    if (attempt >= MAX_POLL_ATTEMPTS) {
      setPolling(false);
      pollingRef.current = false;
      toast.error("Payment status check timed out. Refresh in a moment to see your order.");
      return;
    }
    try {
      const r = await api.get(`/checkout/status/${session_id}`);
      const { payment_status, status, order, product } = r.data;
      if (payment_status === "paid" && order) {
        setPolling(false);
        pollingRef.current = false;
        setOwned((prev) => new Set([...prev, order.product_id]));
        toast.success(
          `Purchased "${product?.name || "item"}" — seller earns ${formatPrice(order.seller_cut_cents)}, NoteLosh takes ${formatPrice(order.commission_cents)} (20%).`
        );
        // Clean URL
        navigate("/market", { replace: true });
        return;
      }
      if (status === "expired" || payment_status === "unpaid" && status === "complete") {
        setPolling(false);
        pollingRef.current = false;
        toast.error("Payment was not completed.");
        navigate("/market", { replace: true });
        return;
      }
      setTimeout(() => pollStatus(session_id, attempt + 1), POLL_INTERVAL_MS);
    } catch {
      setTimeout(() => pollStatus(session_id, attempt + 1), POLL_INTERVAL_MS);
    }
  }, [navigate]);

  // Handle return from Stripe (?session_id=...) or cancellation (?cancelled=1)
  useEffect(() => {
    const cancelled = searchParams.get("cancelled");
    if (cancelled) {
      toast.message("Checkout cancelled");
      navigate("/market", { replace: true });
      return;
    }
    if (searchParams.get("boost_cancelled")) {
      toast.message("Boost cancelled");
      navigate("/market", { replace: true });
      return;
    }
    const boostSid = searchParams.get("boost_session_id");
    if (boostSid) {
      (async () => {
        try {
          const r = await api.get(`/boost/status/${boostSid}`);
          if (r.data?.payment_status === "paid") {
            toast.success("Your note is now promoted");
            try {
              const fresh = await api.get("/products", { params: listParams() });
              setProducts(fresh.data);
            } catch { /* ignore */ }
          } else {
            toast.message("Finishing up your boost…");
          }
        } catch {
          toast.error("Could not confirm boost");
        } finally {
          navigate("/market", { replace: true });
        }
      })();
      return;
    }
    const sid = searchParams.get("session_id");
    if (sid && !pollingRef.current) {
      pollingRef.current = true;
      setPolling(true);
      pollStatus(sid, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Deep-link from a notification: scroll the product into view once it's
  // on the page (comments auto-open via `forceOpen` on ProductComments).
  useEffect(() => {
    if (!highlightProductId || highlightedRef.current || loading) return;
    const el = document.querySelector(`[data-testid="product-${highlightProductId}"]`);
    if (el) {
      highlightedRef.current = true;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightProductId, loading, products]);

  const buy = async (p) => {
    // Already in the user's workspace (either a purchased clone or the
    // seller's own published original) → do nothing, keep the Owned pill.
    if (owned.has(p.product_id) || p.owned_by_me) {
      toast.info(`You already own "${p.name}"`);
      return;
    }
    const isFreeForMe = p.free_for_me || (p.price_cents === 0);
    if (isFreeForMe) {
      setBusyId(p.product_id);
      try {
        await api.post(`/products/${p.product_id}/claim-free`);
        setOwned((prev) => new Set([...prev, p.product_id]));
        toast.success(`Added "${p.name}" to your workspace`);
      } catch (err) {
        toast.error(err?.response?.data?.detail || err?.message || "Could not claim");
      } finally { setBusyId(null); }
      return;
    }
    if (native) {
      // Google Play must handle billing directly — bypass the wallet dialog on native.
      setBusyId(p.product_id);
      try {
        await payments.buy(p);
        setOwned((prev) => new Set([...prev, p.product_id]));
        toast.success(`Purchased "${p.name}"`);
      } catch (err) {
        toast.error(err?.response?.data?.detail || err?.message || "Could not start checkout");
      } finally { setBusyId(null); }
      return;
    }
    // Web / PWA — let the user choose wallet vs card
    setPayDialogProduct(p);
  };

  const payFromWallet = async (p) => {
    setBusyId(p.product_id);
    try {
      const r = await api.post(`/wallet/pay/${p.product_id}`);
      setOwned((prev) => new Set([...prev, p.product_id]));
      const cut = r.data?.order?.seller_cut_cents;
      toast.success(
        `Paid "${p.name}" from wallet${cut ? ` — seller earns €${(cut/100).toFixed(2)} (80%)` : ""}`
      );
      window.dispatchEvent(new CustomEvent("notelosh:wallet-changed"));
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 402) toast.error("Not enough wallet balance — top up or pay with card");
      else toast.error(detail || "Payment failed");
      throw err;
    } finally { setBusyId(null); }
  };

  const payWithCard = async (p) => {
    setBusyId(p.product_id);
    try {
      await payments.buy(p);
      // Stripe redirect; busy state clears via unmount
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Could not start checkout");
      setBusyId(null);
      throw err;
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F3F4F6]">
      <header className="px-3 sm:px-6 py-4 border-b border-[#1E1E1E] flex items-center justify-between gap-2 sticky top-0 bg-[#0A0A0A]/90 backdrop-blur z-10">
        <div className="flex items-center gap-1 sm:gap-3 min-w-0">
          <Link to="/app" data-testid="market-back-link">
            <Button variant="ghost" size="icon" className="text-[#9CA3AF] hover:bg-[#1E1E1E]">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="font-heading text-lg sm:text-xl font-bold flex items-center gap-2 min-w-0">
            <ShoppingBag className="h-5 w-5 text-[#3B82F6]" /> Marketplace
          </h1>
        </div>
        <form onSubmit={lookupCode} className="flex items-center gap-1.5 sm:gap-2">
          <CommunitySheet onBuy={buy} owned={owned} />
          <div className="relative">
            <Ticket className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6B7280]" />
            <input
              data-testid="market-code-input"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.toUpperCase().replace(/[^A-Z2-9]/g, ""))}
              maxLength={6}
              placeholder="Invite code"
              className="pl-7 pr-2 h-8 w-[5.5rem] sm:w-32 bg-[#1E1E1E] border border-[#27272A] rounded-full text-xs text-[#F3F4F6] placeholder:text-[#6B7280] focus:outline-none focus:border-[#3B82F6] tracking-wider font-mono"
            />
          </div>
          <Button
            type="submit"
            data-testid="market-code-submit-btn"
            disabled={!codeInput.trim() || codeBusy}
            className="h-8 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-xs px-3"
          >
            {codeBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Find"}
          </Button>
        </form>
      </header>

      {inGroup ? <GroupsBar groups={myGroups} groupId={groupId} setGroupId={setGroupId} onSearch={setGroupQuery} setDirQuery={setDirQuery} />
        : <MarketSearchBar value={query} onSearch={setQuery} scope={category === "all" ? null : category === "following" ? "shops you follow" : category === "suggested" ? null : CATEGORY_LABEL[category]} />}

      {/* Category filter row */}
      <div className="px-6 pt-4 flex items-center gap-1.5 overflow-x-auto no-scrollbar">
        <button
          data-testid="market-cat-all"
          onClick={() => setCategory("all")}
          className={`shrink-0 px-3 py-1.5 text-xs rounded-full transition-all border ${
            category === "all"
              ? "bg-[#2563EB] border-[#2563EB] text-white"
              : "bg-[#1E1E1E] border-[#27272A] text-[#9CA3AF] hover:text-[#F3F4F6]"
          }`}
        >
          All
        </button>
        {CATEGORIES.map(({ id, label, Icon }) => (
          <button
            key={id}
            data-testid={`market-cat-${id}`}
            onClick={() => setCategory(id)}
            className={`shrink-0 px-3 py-1.5 text-xs rounded-full transition-all border flex items-center gap-1.5 ${
              category === id
                ? "bg-[#2563EB] border-[#2563EB] text-white"
                : "bg-[#1E1E1E] border-[#27272A] text-[#9CA3AF] hover:text-[#F3F4F6]"
            }`}
          >
            <Icon className="h-3 w-3" />
            {label}
          </button>
        ))}
        <GroupsChip active={inGroup} onClick={() => setCategory("group")} />
        <FollowingChip active={category === "following"} onClick={() => { if (category !== "following") setSort("newest"); setCategory("following"); }} />
        <SuggestedChip active={category === "suggested"} onClick={() => setCategory("suggested")} />
      </div>

      {inGroup && <GroupsDirectory groups={myGroups} groupId={groupId} dirQuery={dirQuery} setGroupId={setGroupId}
        onGroupUpdated={(u) => setMyGroups((l) => l.map((g) => (g.group_id === u.group_id ? u : g)))} />}

      {polling && (
        <div
          data-testid="checkout-polling-banner"
          className="mx-auto max-w-6xl mt-4 px-4 py-3 rounded-2xl border border-[#2563EB]/50 bg-[#2563EB]/10 text-[#D1D5DB] text-sm flex items-center gap-3"
        >
          <Loader2 className="h-4 w-4 animate-spin text-[#3B82F6]" />
          Confirming your payment with Stripe…
        </div>
      )}

      <div className="px-6 py-8 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
        <main className="space-y-8">
          {category === "suggested" ? <SuggestedFeed /> : (<>
          {codeMatch && (
            <section data-testid="code-match-section">
              <h3 className="text-[10px] uppercase tracking-wider text-[#3B82F6] mb-3 flex items-center gap-1.5">
                <Ticket className="h-3.5 w-3.5" /> Found via invite code
              </h3>
              <article
                data-testid={`code-match-${codeMatch.product_id}`}
                className="rounded-2xl bg-[#121212] border border-[#3B82F6]/60 overflow-hidden relative"
              >
                <button
                  data-testid="code-match-dismiss-btn"
                  onClick={clearCodeMatch}
                  aria-label="Dismiss"
                  className="absolute top-2 right-2 z-10 h-7 w-7 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white/80 hover:text-white transition-colors"
                >
                  <XIcon className="h-3.5 w-3.5" />
                </button>
                <div className="grid sm:grid-cols-[200px_1fr]">
                  <div className="h-36 sm:h-full w-full bg-[#1E1E1E] overflow-hidden relative">
                    <ProductCover p={codeMatch} />
                    <SaveButton productId={codeMatch.product_id} saved={saved} setSaved={setSaved} className="top-2 left-2" />
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#3B82F6]">
                      {CATEGORY_LABEL[codeMatch.category] || "Note"} · code {codeMatch.share_code}
                    </div>
                    <h3 className="font-heading font-semibold text-lg leading-tight">{codeMatch.name}</h3>
                    <p className="text-xs text-[#9CA3AF] line-clamp-3">{codeMatch.description}</p>
                    <WhatsInside p={codeMatch} />
                    <OwnershipDisclaimer product={codeMatch} />
                    <Link
                      to={`/seller/${codeMatch.seller_id}`}
                      data-testid={`code-match-seller-${codeMatch.product_id}`}
                      className="inline-flex items-center gap-1 text-[11px] text-[#3B82F6] hover:text-[#60A5FA]"
                    >
                      <Store className="h-3 w-3" /> by {codeMatch.seller_name}
                      {codeMatch.seller_verified && (
                        <BadgeCheck
                          data-testid={`code-match-seller-verified-${codeMatch.product_id}`}
                          className="h-3 w-3 text-[#3B82F6]"
                          aria-label="Verified seller"
                        />
                      )}
                    </Link>
                    <div className="flex items-center justify-between pt-2">
                      <span className={`text-base font-semibold ${codeMatch.price_cents === 0 ? "text-[#10B981]" : ""}`}>
                        {codeMatch.price_cents === 0 ? "Free" : formatPrice(codeMatch.price_cents)}
                      </span>
                      <Button
                        onClick={() => buy(codeMatch)}
                        disabled={busyId === codeMatch.product_id || owned.has(codeMatch.product_id)}
                        data-testid={`code-match-buy-${codeMatch.product_id}`}
                        className={`rounded-full text-white ${
                          (codeMatch.free_for_me || codeMatch.price_cents === 0)
                            ? "bg-[#10B981] hover:bg-[#059669]"
                            : "bg-[#2563EB] hover:bg-[#1D4ED8]"
                        }`}
                      >
                        {owned.has(codeMatch.product_id)
                          ? "Owned"
                          : busyId === codeMatch.product_id
                            ? "Processing…"
                            : (codeMatch.free_for_me || codeMatch.price_cents === 0)
                              ? "Get free"
                              : "Buy"}
                      </Button>
                    </div>
                  </div>
                </div>
              </article>
            </section>
          )}
          {query && !inGroup && category === "all" && <CreatorResults creators={creators} />}
          {!query && !inGroup && trending.length > 0 && (
            <section data-testid="trending-section">
              <h3 className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-3 flex items-center gap-1.5">
                <Flame className="h-3.5 w-3.5 text-[#F59E0B]" /> Trending this week
              </h3>
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin">
                {trending.map(p => (
                  <article
                    key={p.product_id}
                    data-testid={`trending-${p.product_id}`}
                    className="shrink-0 w-56 rounded-2xl bg-[#121212] border border-[#1E1E1E] overflow-hidden hover:border-[#2563EB]/60 transition-all"
                  >
                    <div className="h-24 w-full bg-[#1E1E1E] overflow-hidden relative">
                      <ProductCover p={p} />
                      <SaveButton productId={p.product_id} saved={saved} setSaved={setSaved} className="top-1.5 left-1.5 !h-7 !w-7" />
                      {p.recent_sold > 0 && (
                        <span className="absolute top-1.5 right-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-[#F59E0B]/20 border border-[#F59E0B]/40 text-[#F59E0B]">
                          {p.recent_sold} sold · 7d
                        </span>
                      )}
                    </div>
                    <div className="p-3 space-y-1.5">
                      <h4 className="font-heading text-sm font-semibold leading-tight line-clamp-1">{p.name}</h4>
                      <Link
                        to={`/seller/${p.seller_id}`}
                        data-testid={`trending-seller-${p.product_id}`}
                        className="flex items-center gap-1 text-[10px] text-[#3B82F6] hover:text-[#60A5FA] truncate"
                      >
                        <span className="truncate">by {p.seller_name}</span>
                        {p.seller_verified && (
                          <BadgeCheck
                            data-testid={`trending-seller-verified-${p.product_id}`}
                            className="h-3 w-3 text-[#3B82F6] shrink-0"
                            aria-label="Verified seller"
                          />
                        )}
                      </Link>
                      <div className="flex items-center justify-between pt-1">
                        <span className="text-sm font-semibold">{formatPrice(p.price_cents)}</span>
                        <Button
                          onClick={() => buy(p)}
                          disabled={busyId===p.product_id || owned.has(p.product_id)}
                          data-testid={`trending-buy-${p.product_id}`}
                          className="rounded-full h-7 px-3 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[11px]"
                        >
                          {owned.has(p.product_id) ? "Owned" : busyId===p.product_id ? "…" : "Buy"}
                        </Button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          <section className={inGroup && !groupOpen ? "hidden" : ""}>
            <div className="mb-3 flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <h3 className="text-[10px] uppercase tracking-wider text-[#6B7280] flex items-center gap-1.5 min-w-0 max-w-full overflow-hidden whitespace-nowrap">
              <ShoppingBag className="h-3.5 w-3.5 shrink-0" /> {(inGroup ? groupQuery : query) ? <span data-testid="market-search-results-title" className="truncate">Results for “{inGroup ? groupQuery : query}”</span> : inGroup ? "Notes in this group" : category === "following" ? "From shops you follow" : "All listings"}
            </h3>
            <div className="flex items-center gap-2">
              <PriceFilter value={price} onChange={setPrice} />
              <MarketSort value={sort} onChange={setSort} />
            </div>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-20 text-[#6B7280]">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />Loading…
              </div>
            ) : products.length === 0 && !inGroup && (query || Object.keys(priceParams(price)).length) ? (
              <div data-testid="market-search-empty" className="rounded-2xl bg-[#121212] border border-dashed border-[#27272A] py-14 px-6 text-center">
                <h4 className="font-heading text-lg font-semibold text-[#F3F4F6] mb-1.5">{query ? `No notes match “${query}”` : "No notes in this price range"}</h4>
                <p className="text-sm text-[#9CA3AF]">Try another word, a wider price range{category !== "all" ? " or the All category" : ""}.</p>
              </div>
            ) : products.length === 0 && inGroup ? (
              <div data-testid="market-group-empty" className="rounded-2xl bg-[#121212] border border-dashed border-[#4C1D95] py-14 px-6 text-center">
                <h4 className="font-heading text-lg font-semibold text-[#F3F4F6] mb-1.5">{groupQuery ? `No group notes match “${groupQuery}”` : "No notes in this group yet"}</h4>
                <p className="text-sm text-[#9CA3AF]">Members publish here by choosing this group in the Publish window.</p>
              </div>
            ) : products.length === 0 && category === "following" ? (
              <div data-testid="market-following-empty" className="rounded-2xl bg-[#121212] border border-dashed border-[#27272A] py-14 px-6 text-center">
                <h4 className="font-heading text-lg font-semibold text-[#F3F4F6] mb-1.5">Nothing from shops you follow yet</h4>
                <p className="text-sm text-[#9CA3AF]">Follow sellers from their shop page and their newest notes show up here.</p>
              </div>
            ) : products.length === 0 ? (
              <div
                data-testid="marketplace-empty-state"
                className="rounded-2xl bg-[#121212] border border-dashed border-[#27272A] py-16 px-6 text-center"
              >
                <div className="mx-auto h-12 w-12 rounded-full bg-[#1E1E1E] border border-[#27272A] flex items-center justify-center mb-4">
                  <ShoppingBag className="h-5 w-5 text-[#3B82F6]" />
                </div>
                <h4 className="font-heading text-lg font-semibold text-[#F3F4F6] mb-1.5">The marketplace is quiet</h4>
                <p className="text-sm text-[#9CA3AF] max-w-sm mx-auto">
                  No note packs are listed yet. Publish one of your own notes to be the first — head back to your workspace and tap <span className="text-[#F3F4F6] font-medium">Publish</span> on any thread.
                </p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {products.map(p => {
                  const boost = p.boost;
                  const tierColor = boost ? ({ bronze: "#B45309", silver: "#9CA3AF", gold: "#E7B008" }[boost.tier] || "#9CA3AF") : null;
                  return (
                  <article
                    key={p.product_id}
                    data-testid={`product-${p.product_id}`}
                    data-boost={boost ? boost.tier : undefined}
                    className={`rounded-2xl bg-[#121212] border overflow-hidden transition-all ${
                      p.product_id === highlightProductId
                        ? "border-[#10B981] ring-2 ring-[#10B981]/40"
                        : boost
                        ? "border-[#1E1E1E] hover:border-[#27272A]"
                        : "border-[#1E1E1E] hover:border-[#27272A]"
                    }`}
                    style={boost ? { boxShadow: `inset 0 0 0 1px ${tierColor}22` } : undefined}
                  >
                    <div className="h-36 w-full bg-[#1E1E1E] overflow-hidden relative">
                      <ProductCover p={p} />
                      <SaveButton productId={p.product_id} saved={saved} setSaved={setSaved} />
                      {boost && (
                        <span
                          data-testid={`product-boost-${p.product_id}`}
                          title={`${boost.tier.charAt(0).toUpperCase() + boost.tier.slice(1)} promoted`}
                          aria-label={`${boost.tier} promoted`}
                          className="absolute top-2 left-2 flex items-center gap-1 rounded-full bg-black/55 backdrop-blur-sm px-1.5 py-0.5"
                        >
                          <span className="h-1.5 w-1.5 rounded-full" style={{ background: tierColor }} />
                        </span>
                      )}
                    </div>
                    <div className="p-4 space-y-2">
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#3B82F6]">
                        Note
                      </div>
                      <h3 className="font-heading font-semibold text-base leading-tight flex items-center gap-1.5">
                        {p.name}
                        {p.verified && (
                          <BadgeCheck
                            data-testid={`verified-badge-${p.product_id}`}
                            className="h-4 w-4 text-[#10B981] shrink-0"
                            aria-label="Verified"
                          />
                        )}
                      </h3>
                      <p className="text-xs text-[#9CA3AF] line-clamp-2">{p.description}</p>
                      <WhatsInside p={p} />
                      <OwnershipDisclaimer product={p} />
                      {p.audience === "invited" && (
                        <span
                          data-testid={`product-private-${p.product_id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/40 px-2 py-0.5 text-[10px] text-[#FBBF24] w-fit"
                        >
                          <Lock className="h-2.5 w-2.5" /> Private · invited only
                        </span>
                      )}
                      <Link
                        to={`/seller/${p.seller_id}`}
                        data-testid={`product-seller-${p.product_id}`}
                        className="inline-flex items-center gap-1 text-[11px] text-[#3B82F6] hover:text-[#60A5FA]"
                      >
                        <Store className="h-3 w-3" /> by {p.seller_name}
                        {p.seller_verified && (
                          <BadgeCheck
                            data-testid={`product-seller-verified-${p.product_id}`}
                            className="h-3 w-3 text-[#3B82F6]"
                            aria-label="Verified seller"
                          />
                        )}
                      </Link>
                      {/* Likes / dislikes row — buyers vote, everyone sees the counts. */}
                      {(() => {
                        const isSeller = !!(myUserId && p.seller_id === myUserId);
                        const canRate = !isSeller && (owned.has(p.product_id) || p.owned_by_me);
                        const title = isSeller
                          ? "You can't rate your own note"
                          : canRate ? "" : "Only buyers can rate";
                        return (
                      <div className="flex items-center gap-1 pt-1">
                        <button
                          type="button"
                          data-testid={`like-${p.product_id}`}
                          onClick={() => voteOn(p, "like")}
                          disabled={!canRate}
                          title={title || "Like"}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] border transition-all ${
                            p.my_vote === "like"
                              ? "border-[#10B981] bg-[#10B981]/15 text-[#10B981]"
                              : "border-[#27272A] text-[#9CA3AF] hover:text-[#F3F4F6] hover:border-[#3F3F46]"
                          } disabled:opacity-40 disabled:cursor-not-allowed`}
                        >
                          <ThumbsUp className="h-3 w-3" /> {p.likes || 0}
                        </button>
                        <button
                          type="button"
                          data-testid={`dislike-${p.product_id}`}
                          onClick={() => voteOn(p, "dislike")}
                          disabled={!canRate}
                          title={title || "Dislike"}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] border transition-all ${
                            p.my_vote === "dislike"
                              ? "border-[#EF4444] bg-[#EF4444]/15 text-[#EF4444]"
                              : "border-[#27272A] text-[#9CA3AF] hover:text-[#F3F4F6] hover:border-[#3F3F46]"
                          } disabled:opacity-40 disabled:cursor-not-allowed`}
                        >
                          <ThumbsDown className="h-3 w-3" /> {p.dislikes || 0}
                        </button>
                        {!p.verified && (p.likes || 0) > 0 && (
                          <span className="ml-auto text-[10px] text-[#6B7280]">
                            {Math.max(0, 20 - p.likes)} likes to Verified
                          </span>
                        )}
                      </div>
                        );
                      })()}
                      <div className="flex items-center justify-between pt-2">
                        <span className={`text-sm font-semibold ${p.price_cents === 0 ? "text-[#10B981]" : ""}`}>
                          {p.price_cents === 0 ? "Free" : formatPrice(p.price_cents)}
                        </span>
                        <Button
                          onClick={() => buy(p)}
                          disabled={busyId===p.product_id || owned.has(p.product_id)}
                          data-testid={`buy-${p.product_id}`}
                          className={`rounded-full h-8 text-white text-xs ${
                            (p.free_for_me || p.price_cents === 0)
                              ? "bg-[#10B981] hover:bg-[#059669]"
                              : "bg-[#2563EB] hover:bg-[#1D4ED8]"
                          }`}
                        >
                          {owned.has(p.product_id) ? (
                            <><Check className="h-3 w-3 mr-1"/>Owned</>
                          ) : busyId===p.product_id ? (
                            <><Loader2 className="h-3 w-3 mr-1 animate-spin"/>Opening…</>
                          ) : (p.free_for_me || p.price_cents === 0) ? (
                            <><Gift className="h-3 w-3 mr-1"/>Get free</>
                          ) : (
                            "Buy"
                          )}
                        </Button>
                      </div>
                      <ProductComments
                        product={p}
                        forceOpen={p.product_id === highlightProductId}
                        canComment={
                          !!myUserId && (
                            p.seller_id === myUserId ||
                            owned.has(p.product_id) ||
                            p.owned_by_me
                          )
                        }
                        onCountChange={(pid, n) => {
                          setProducts((prev) => prev.map((x) => x.product_id === pid ? { ...x, comment_count: n } : x));
                        }}
                      />
                      {myUserId && p.seller_id !== myUserId && (owned.has(p.product_id) || p.owned_by_me) && (
                        <button
                          type="button"
                          data-testid={`report-product-${p.product_id}`}
                          onClick={() => setReportTarget({ type: "product", id: p.product_id, label: p.name })}
                          className="inline-flex items-center gap-1 text-[10px] text-[#6B7280] hover:text-[#EF4444] transition-colors pt-1"
                        >
                          <Flag className="h-3 w-3" /> Report listing
                        </button>
                      )}
                    </div>
                  </article>
                  );
                })}
              </div>
            )}
          </section>
          </>)}
        </main>
        <aside className="space-y-4">
          <AdSlot slot="market-side-1"/>
          <AdSlot slot="market-side-2"/>
        </aside>
      </div>
      <PayFromWalletDialog
        open={!!payDialogProduct}
        onOpenChange={(o) => !o && setPayDialogProduct(null)}
        product={payDialogProduct}
        wallet={wallet}
        onPayWallet={payFromWallet}
        onPayCard={payWithCard}
      />
      <ReportDialog
        open={!!reportTarget}
        onOpenChange={(o) => !o && setReportTarget(null)}
        targetType={reportTarget?.type}
        targetId={reportTarget?.id}
        label={reportTarget?.label}
      />
    </div>
  );
}

function formatPrice(c) { return `$${(c/100).toFixed(2)}`; }
